import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Info } from "lucide-react";
import { type ApplyDueDateRow, type WorkflowTemplate } from "~/api/types";
import { applyWorkflowTemplate } from "~/api/v2/requests";
import { mutateDocumentReleases } from "~/api/v2/useDocumentReleases";
import { Button } from "~/atoms/button";
import { toISODateTimeString } from "~/helpers/date";
import {
  useApplyTemplateRows,
  type ApplyTemplateRow,
} from "~/hooks/useApplyTemplateRows";
import { type WorkflowDates } from "~/hooks/useWorkflowDatesEditor";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import { ApplyTemplateDialog } from "./ApplyTemplateDialog";
import { ApplyTemplateTable } from "./ApplyTemplateTable";

interface Props {
  template: WorkflowTemplate | null;
}
export function TemplateApplyTab({ template }: Props) {
  const { t } = useTranslation();

  const { rows, tagMap, isLoading } = useApplyTemplateRows();

  const [isApplying, setIsApplying] = useState(false);
  const [stage, setStage] = useState<null | "confirm" | "wizard">(null);
  const [pending, setPending] = useState<{
    rows: ApplyTemplateRow[];
    clearSelection: () => void;
  } | null>(null);

  const apply = async (
    workflowDates: Record<number, WorkflowDates>,
    keepDescriptionIds: number[],
  ) => {
    if (!template || !pending) return;

    setIsApplying(true);

    const artifactIds = pending.rows.map((row) => row.id);
    const dueDates: Record<number, ApplyDueDateRow> = {};
    for (const artifactId of artifactIds) {
      const dates = workflowDates[artifactId];
      if (!dates) continue;

      dueDates[artifactId] = {
        reviewFrom: toISODateTimeString(dates.reviewFrom),
        reviewTo: toISODateTimeString(dates.reviewTo),
        stepDueDates: dates.stepDueDates.map(toISODateTimeString),
      };
    }

    try {
      await applyWorkflowTemplate(template.id, {
        artifactIds,
        dueDates,
        keepDescriptionArtifactIds: keepDescriptionIds,
      });
      await mutateDocumentReleases();
      pending.clearSelection();
      // Only closed once it worked, so a failure leaves every date the user
      // entered still on screen to retry.
      setStage(null);
      toast.success(t(($) => $.workflowTemplates.applySuccess));
    } catch {
      toast.error(t(($) => $.workflowTemplates.applyError));
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <ApplyTemplateTable
        templateId={template?.id ?? null}
        rows={rows}
        tagMap={tagMap}
        isLoading={isLoading}
        actions={(selection, selectedRows) => {
          const overwrittenCount = selectedRows.filter(
            (row) => row.currentTemplateId !== null,
          ).length;

          return (
            <>
              {!template && (
                <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
                  <Info className="size-4 shrink-0" />
                  {t(
                    ($) => $.workflowTemplates.documentsTable.saveTemplateFirst,
                  )}
                </span>
              )}
              <Button
                onClick={() => {
                  if (!template || selectedRows.length === 0) return;
                  setPending({
                    rows: selectedRows,
                    clearSelection: selection.clearSelection,
                  });
                  setStage(overwrittenCount > 0 ? "confirm" : "wizard");
                }}
                disabled={!template || selectedRows.length === 0 || isApplying}
              >
                {isApplying
                  ? t(($) => $.workflowTemplates.applying)
                  : t(($) => $.workflowTemplates.applyToDocuments, {
                      count: selectedRows.length,
                    })}
              </Button>
            </>
          );
        }}
      />

      <ConfirmDialog
        open={stage === "confirm"}
        onOpenChange={(open) => {
          if (!open)
            setStage((current) => (current === "confirm" ? null : current));
        }}
        title={t(($) => $.workflowTemplates.applyConfirm.title)}
        description={t(($) => $.workflowTemplates.applyConfirm.description, {
          count: (pending?.rows ?? []).filter(
            (row) => row.currentTemplateId !== null,
          ).length,
        })}
        confirmLabel={t(($) => $.workflowTemplates.applyConfirm.confirm)}
        onConfirm={() => setStage("wizard")}
      />

      <ApplyTemplateDialog
        open={stage === "wizard"}
        onOpenChange={(open) => setStage(open ? "wizard" : null)}
        templateName={
          template?.name ?? t(($) => $.workflowTemplates.defaultBadge)
        }
        templateDescription={template?.description ?? null}
        steps={template?.steps ?? []}
        rows={pending?.rows ?? []}
        onApply={apply}
        isApplying={isApplying}
      />
    </>
  );
}
